import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AppShell } from "@/components/Nav";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Check, Sparkles, Zap, ShieldCheck, ArrowUpRight, Crown, ScanLine } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const BASIC_FEATURES = [
  "Unlimited AI credit report scans",
  "Unlimited AI dispute letters",
  "All 6 dispute strategies + FCRA citations",
  "Standard AI coach (refresh every 24h)",
  "Cert-mail helper (creditor address lookup)",
  "Email dispute reminders",
  "Score history + progress tracking",
  "Priority letter drafting queue",
];

const PLUS_FEATURES = [
  { text: "Everything in Basic — unlimited & unlocked", highlight: true },
  { text: "Priority AI coach (refresh anytime)" },
  { text: "Mortgage-readiness score simulator" },
  { text: "CFPB escalation letter templates" },
  { text: "SMS dispute reminders + status pings" },
  { text: "Identity-theft assist kit (FTC affidavit + block letters)" },
  { text: "Concierge letter review by senior AI agent" },
  { text: "Advanced credit utilization simulator" },
  { text: "Priority email support (12h response)" },
];

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/pricing");
        setPricing(data.prices);
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const checkout = async (lookup_key) => {
    setCheckingOut(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key,
        quantity: 1,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err));
      setCheckingOut(null);
    }
  };

  const basicPrice = pricing?.find((p) => p.lookup_key === "aura_basic_monthly");
  const plusPrice = pricing?.find((p) => p.lookup_key === "aura_plus_monthly");

  const plan = user?.plan || "free";
  const isBasic = plan === "basic";
  const isPlus = plan === "plus";
  const isSubscribed = isBasic || isPlus;

  const priceStr = (p) => (p ? (p.price_cents / 100).toFixed(2).replace(/\.00$/, "") : "—");

  return (
    <AppShell>
      <div className="mb-10">
        <div className="overline mb-3">Pricing</div>
        <h1 className="font-display text-5xl md:text-6xl leading-none">
          Pick your
          <span className="text-[#D5FF3F]"> package.</span>
        </h1>
        <p className="text-[#A8ADA5] mt-3 max-w-2xl">
          Unlock <span className="text-[#F5F3EE]">Basic</span> for unlimited scans, unlimited dispute
          letters &amp; certified mail, or go <span className="text-[#D5FF3F]">Plus</span> for the works —
          mortgage sim, CFPB escalation, ID-theft kit, concierge review.
        </p>
      </div>

      {/* Free scan note — no plan, no drafts, just the initial credit read */}
      <div
        className="mb-8 card-surface p-5 flex items-start gap-4"
        data-testid="pricing-free-scan-note"
      >
        <div className="w-10 h-10 rounded-sm bg-[#D5FF3F]/10 border border-[#D5FF3F]/25 flex items-center justify-center flex-shrink-0">
          <ScanLine size={18} className="text-[#D5FF3F]" strokeWidth={2} />
        </div>
        <div className="flex-1">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#D5FF3F] mb-1">
            Free · scan only
          </div>
          <p className="text-sm text-[#A8ADA5] leading-relaxed">
            Anyone can register and scan their credit report to see exactly what&rsquo;s dragging their
            score down. Drafting dispute letters and mailing them USPS Certified requires a package
            below.
          </p>
        </div>
      </div>

      {isSubscribed && (
        <div className="mb-8 card-surface p-5 flex items-center justify-between gap-4 border-[#D5FF3F]/40" data-testid="pricing-current-plan">
          <div>
            <div className="overline mb-1 text-[#D5FF3F]">
              You&rsquo;re on Aura {isPlus ? "Plus" : "Basic"}
            </div>
            <div className="text-sm text-[#A8ADA5]">
              {user?.pro_until && (
                <>Access until <span className="text-[#F5F3EE] font-mono">{new Date(user.pro_until).toLocaleDateString()}</span></>
              )}
            </div>
          </div>
          <Link to="/app/billing">
            <Button className="rounded-full btn-outline">Manage billing</Button>
          </Link>
        </div>
      )}

      {loading ? (
        <div className="text-[#A8ADA5]">Loading…</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-px bg-[#26282C] border border-[#26282C]">
          {/* Basic */}
          <div className="bg-[#0B0C0E] p-8 flex flex-col relative" data-testid="tier-basic">
            <div className="absolute top-4 right-4">
              <span className="text-[10px] px-2 py-0.5 rounded-full font-mono uppercase tracking-widest bg-[#F5F3EE] text-[#0B0C0E]">
                <Sparkles size={10} className="inline mr-1" /> Most popular
              </span>
            </div>
            <div className="overline mb-3 text-[#F5F3EE]">Aura Basic</div>
            <div className="flex items-baseline gap-1 mb-1">
              <span className="ticker text-6xl text-[#F5F3EE]">
                ${priceStr(basicPrice)}
              </span>
              <span className="text-[#A8ADA5] font-mono text-sm ml-1">/mo</span>
            </div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#7CFF9B] mb-4">
              Monthly · cancel anytime
            </div>
            <p className="text-sm text-[#A8ADA5] mb-6">Everything you need to clean up your credit — no limits.</p>
            <ul className="space-y-3 mb-8 flex-1">
              {BASIC_FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm">
                  <Check size={14} className="text-[#F5F3EE] mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                  <span className="text-[#F5F3EE]">{f}</span>
                </li>
              ))}
            </ul>
            <Button
              disabled={isBasic || checkingOut !== null}
              onClick={() => checkout("aura_basic_monthly")}
              data-testid="checkout-basic-btn"
              className="w-full rounded-full h-11 btn-outline hover:!bg-[#F5F3EE] hover:!text-[#0B0C0E] hover:!border-[#F5F3EE]"
            >
              {isBasic ? "Your plan" : checkingOut ? "Redirecting…" : (
                <>Get Basic <ArrowUpRight size={15} className="ml-1.5" /></>
              )}
            </Button>
            <div className="flex items-center justify-center gap-2 mt-3 text-[10px] font-mono uppercase tracking-widest text-[#6D716C]">
              <Zap size={11} /> Apple Pay · Google Pay · Card
            </div>
          </div>

          {/* Plus */}
          <div className="bg-[#0B0C0E] p-8 flex flex-col relative border-l border-[#D5FF3F]/20" data-testid="tier-plus">
            <div className="absolute top-4 right-4">
              <span className="text-[10px] px-2 py-0.5 rounded-full font-mono uppercase tracking-widest bg-[#D5FF3F] text-[#0B0C0E]">
                <Crown size={10} className="inline mr-1" /> Everything
              </span>
            </div>
            <div className="overline mb-3 text-[#D5FF3F]">Aura Plus</div>
            <div className="flex items-baseline gap-1 mb-1">
              <span className="ticker text-6xl text-[#D5FF3F]">
                ${priceStr(plusPrice)}
              </span>
              <span className="text-[#A8ADA5] font-mono text-sm ml-1">/mo</span>
            </div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#D5FF3F] mb-4">
              Monthly · cancel anytime
            </div>
            <p className="text-sm text-[#A8ADA5] mb-6">Concierge tools + mortgage sim + escalation firepower.</p>
            <ul className="space-y-3 mb-8 flex-1">
              {PLUS_FEATURES.map((f) => (
                <li key={f.text} className="flex items-start gap-2 text-sm">
                  <Check
                    size={14}
                    className={`mt-0.5 flex-shrink-0 ${f.highlight ? "text-[#D5FF3F]" : "text-[#D5FF3F]"}`}
                    strokeWidth={2.5}
                  />
                  <span className={f.highlight ? "text-[#F5F3EE] font-medium" : "text-[#F5F3EE]"}>
                    {f.text}
                  </span>
                </li>
              ))}
            </ul>
            <Button
              disabled={isPlus || checkingOut !== null}
              onClick={() => checkout("aura_plus_monthly")}
              data-testid="checkout-plus-btn"
              className="w-full rounded-full h-11 btn-lime"
            >
              {isPlus ? "Your plan" : checkingOut ? "Redirecting…" : (
                <>Get Plus <ArrowUpRight size={15} className="ml-1.5" /></>
              )}
            </Button>
            <div className="flex items-center justify-center gap-2 mt-3 text-[10px] font-mono uppercase tracking-widest text-[#6D716C]">
              <Zap size={11} /> Apple Pay · Google Pay · Card
            </div>
          </div>
        </div>
      )}

      {/* Comparison mini-blurb */}
      <div className="mt-8 grid md:grid-cols-2 gap-6">
        <div className="card-surface p-6">
          <div className="overline mb-3">Basic vs Plus in one sentence</div>
          <p className="text-sm text-[#A8ADA5] leading-relaxed">
            <span className="text-[#F5F3EE]">Basic</span> gets your credit report cleaned —
            unlimited scans, unlimited letters, all six strategies. <span className="text-[#D5FF3F]">Plus</span>
            {" "}adds the tools you need <span className="text-[#F5F3EE]">after</span> the cleanup: mortgage
            simulator, CFPB escalation, ID-theft kit, and priority concierge review.
          </p>
        </div>
        <div className="card-surface p-6">
          <div className="overline mb-3">Money-back promise</div>
          <p className="text-sm text-[#A8ADA5] leading-relaxed">
            Not happy? Ask for a full refund within <span className="text-[#D5FF3F]">3 days</span> of purchase —
            no questions asked (CROA 15 U.S.C. § 1679e).
          </p>
        </div>
      </div>

      {/* Fine print — monthly recurring, cancel anytime */}
      <p
        className="mt-6 text-[11px] leading-relaxed text-[#6D716C] max-w-3xl"
        data-testid="pricing-fine-print"
      >
        * Each package is a monthly membership billed automatically until you cancel. Cancel anytime
        from your billing page — you&rsquo;ll keep access until the end of your paid period, then your
        plan drops to free (scan-only). Certified mail letters are billed separately at $12 per letter.
      </p>

      {/* CROA disclosure */}
      <div className="mt-10 card-surface p-8">
        <div className="flex items-start gap-4">
          <ShieldCheck size={22} className="text-[#D5FF3F] flex-shrink-0 mt-1" />
          <div className="text-sm text-[#A8ADA5] leading-relaxed space-y-3">
            <p className="text-[#F5F3EE] font-medium">Your Consumer Credit File Rights Under State and Federal Law</p>
            <p>
              You have a right to dispute inaccurate information in your credit report by contacting the credit
              bureau directly. However, neither you nor any &ldquo;credit repair&rdquo; company or credit repair organization
              has the right to have accurate, current, and verifiable information removed from your credit report.
              The credit bureau must remove accurate, negative information from your report only if it is over seven
              years old. Bankruptcy information can be reported for ten years.
            </p>
            <p>
              You have a right to <span className="text-[#F5F3EE]">obtain a copy of your credit report</span> from
              a credit bureau. You may be charged a reasonable fee. There is no fee, however, if you have been
              turned down for credit, employment, insurance, or a rental dwelling because of information in your
              credit report within the preceding 60 days.
            </p>
            <p>
              You have <span className="text-[#D5FF3F] font-medium">3 days to cancel</span> any Aura Credit
              purchase for a <span className="text-[#F5F3EE] font-medium">full refund</span>, no questions
              asked (Credit Repair Organizations Act, 15 U.S.C. § 1679e).
            </p>
            <p className="text-xs text-[#6D716C]">
              Aura Credit is a software / educational tool. We do not make any promises or guarantees about specific
              credit-score changes or item removals. You may perform any dispute yourself, for free, at any time.
            </p>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
